import {Autocomplete, TextField} from "@mui/material";
import {removeDiacritics} from "./searchHelper";
import {matchSorter} from "match-sorter";
import {useNavigate} from "react-router-dom";
import {useEffect, useMemo, useRef, useState} from "react";

const emptyOption = {id:0, name: 'Žiadne výsledky'};

function CourseSearchLink({ courses, onSelect = null, focusOnOpen = false }) {
    const navigate = useNavigate();
    const [inputValue, setInputValue] = useState(window.search);
    const inputRef = useRef(null);

    const filteredCourses = useMemo(() => {
        const query = inputValue.trim();
        if (query.length < 2) return [];

        const normQuery = removeDiacritics(query);
        const words = normQuery.split(/\s+/);

        // Collect all unique keywords once
        const allKeywords = new Set();
        courses.forEach(course => course.keywords?.forEach(keyword => allKeywords.add(keyword)));
        const keywordList = Array.from(allKeywords);

        // For each typed token → find matching keywords (lenient)
        const tokenMatches = words.map(word =>
            matchSorter(keywordList, word, {
                threshold: matchSorter.rankings.CONTAINS,
            }).filter(keyword => {
                const normKeyword = removeDiacritics(keyword);
                const normWord = word;
                return normKeyword.startsWith(normWord) || normKeyword.includes(normWord) || normWord.includes(normKeyword);
            })
        );

        // ────────────────────────────────
        // ONE WORD → OR mode (any match)
        // ────────────────────────────────
        if (words.length <= 1) {
            const allowed = tokenMatches[0];
            if (allowed.length === 0) return [emptyOption];

            const filteredCourses = matchSorter(courses, normQuery, {
                keys: [
                    (course) => course.code,
                    (course) => course.keywords ? removeDiacritics(course.keywords.join(' ')) : '',
                ],
                threshold: matchSorter.rankings.CONTAINS,
            }).filter(course => {
                const keys = course.keywords.map(removeDiacritics);
                return allowed.some(keyword => {
                    const normKeyword = removeDiacritics(keyword);
                    return keys.some(keyword => keyword === normKeyword || keyword.includes(normKeyword) || normKeyword.includes(keyword));
                });
            });

            if (filteredCourses.length === 0) {
                return [emptyOption]
            }
            return filteredCourses;
        }

        // ────────────────────────────────
        // 2+ WORDS → AND mode (must match every token group)
        // ────────────────────────────────
        const filteredCourses = courses.filter(course => {
            const keys = course.keywords.map(removeDiacritics);
            return tokenMatches.every(group =>
                group.some(keyword => {
                    const normKeyword = removeDiacritics(keyword);
                    return keys.some(keyword => keyword === normKeyword || keyword.includes(normKeyword) || normKeyword.includes(keyword));
                })
            );
        }).sort((a, b) => a.code.localeCompare(b.code, 'sk'));

        if (filteredCourses.length === 0) {
            return [emptyOption]
        }
        return filteredCourses;
    }, [courses, inputValue]);

    useEffect(() => {
        window.search = inputValue;
    }, [inputValue]);

    const getOptionLabel = () => {
        return inputValue;
    };

    const handleChange = (_, selected) => {
        if (!selected || typeof selected === 'string') return;

        const course = selected;
        if (course.id) {
            let url = `/skolenie/${course.id}`;
            if (course.type === 'ISTQB') {
                url = `/skolenia/istqb/${course.id}`;
            }
            navigate(url);

            if (typeof onSelect === "function") {
                onSelect();
            }
        }
    };

    const wrapperStyle = { width: "100%", margin: 0, padding: 0 };

    useEffect(() => {
        if (!focusOnOpen) {
            return;
        }

        const timeoutId = setTimeout(() => {
            inputRef.current?.focus();
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [focusOnOpen]);

    return (
        <div style={wrapperStyle}>
            <Autocomplete
                freeSolo
                options={filteredCourses}
                getOptionLabel={getOptionLabel}
                inputValue={inputValue}
                onInputChange={(_, newValue) => setInputValue(newValue)}
                onChange={handleChange}
                ListboxProps={{
                    sx: {
                        maxHeight: '60vh',
                    },
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        inputRef={inputRef}
                        label="Nájdi školenie"
                        placeholder="Zadaj kľúčové slová ako: CTFL, testing, SQL..."
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                )}
                renderOption={(props, option) => {
                    return (
                        <li {...props} key={option.id}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <strong>{option.name}</strong>
                                {option.keywords?.length > 0 && (
                                    <small style={{ color: '#666', marginTop: 4 }}>
                                        {option.keywords.join(' • ')}
                                    </small>
                                )}
                            </div>
                        </li>
                    )
                }}
            />
        </div>
    );
}

export default CourseSearchLink
